// Home "Continue where you left off" — shows last 3 unique recently
// viewed assets for the current user. Pulled from the /api/search
// empty-query response which already surfaces a `recent` group.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Clock, LayoutDashboard, Database, FolderKanban, Sparkles, ArrowUpRight, Rocket } from "lucide-react";

const KIND_ICON = {
    dashboard: LayoutDashboard,
    dataset: Database,
    workspace: FolderKanban,
    sense_ai_session: Sparkles,
};

export default function ContinueWidget() {
    const { user } = useAuth();
    const [items, setItems] = useState(null);
    const navigate = useNavigate();
    useEffect(() => {
        api.get("/search", { params: { q: "", limit: 6 } })
            .then((r) => setItems((r.data?.groups?.recent || []).slice(0, 3)))
            .catch(() => setItems([]));
    }, []);

    // Empty state — render a friendly welcome card instead of collapsing the
    // grid cell to whitespace. Keeps the Home layout visually balanced for
    // first-time / super-admin users who haven't touched anything yet.
    if (items && items.length === 0) {
        const firstName = user?.name?.split(" ")[0] || "there";
        const shortcuts = [
            { label: "Open a dashboard", route: "/dashboards", icon: LayoutDashboard },
            { label: "Upload data", route: "/data?tab=load", icon: Database },
            { label: "Ask Sense AI", route: "/sense-ai", icon: Sparkles },
        ];
        return (
            <div className="border border-border rounded-sm bg-gradient-to-br from-primary/5 to-transparent p-4 h-full flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5"
                 data-testid="home-widget-continue-empty">
                <div className="flex items-center gap-3 w-full sm:w-auto sm:contents">
                    <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                        <Rocket className="h-5 w-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium leading-snug">
                            Welcome, {firstName}. Pick up where you'll start.
                        </div>
                        <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
                            Your "Continue where you left off" list will populate here once you've opened anything recently.
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:shrink-0 w-full sm:w-auto">
                    {shortcuts.map(({ label, route, icon: Ic }) => (
                        <button key={label}
                            onClick={() => navigate(route)}
                            data-testid={`home-continue-shortcut-${label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-border rounded-sm text-xs bg-card hover:border-primary hover:bg-primary/5 transition-colors">
                            <Ic className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                            {label}
                        </button>
                    ))}
                </div>
            </div>
        );
    }
    return (
        <div className="border border-border rounded-sm bg-card p-4 h-full" data-testid="home-widget-continue">
            <div className="flex items-center gap-1.5 mb-3">
                <Clock className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
                <span className="eyebrow text-[10px]">Continue where you left off</span>
            </div>
            {items === null ? (
                <div className="text-xs text-muted-foreground py-4">Loading…</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    {items.map((it) => {
                        const inferredKind = it.route?.startsWith("/dashboards")
                            ? "dashboard"
                            : it.route?.startsWith("/data")
                                ? "dataset"
                                : it.route?.startsWith("/workspace")
                                    ? "workspace"
                                    : "sense_ai_session";
                        const Icon = KIND_ICON[inferredKind] || Clock;
                        return (
                            <button key={`${it.id}`}
                                onClick={() => navigate(it.route)}
                                className="group flex items-center gap-3 p-3 border border-border rounded-sm bg-secondary/30 hover:bg-secondary/60 hover:border-primary/50 text-left transition-colors"
                                data-testid={`home-continue-item-${it.id}`}>
                                <div className="w-8 h-8 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                                    <Icon className="h-4 w-4 text-primary" strokeWidth={1.5} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{it.title}</div>
                                    <div className="text-[10px] font-mono uppercase text-muted-foreground/80">{it.subtitle || "Resume"}</div>
                                </div>
                                <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
